import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { AssetRow, AssetStatus, buildAssetRows, UNASSIGNED_AREA } from '../listHelpers';
import AssetStatusPill from './AssetStatusPill';

interface AssetListViewProps {
  templates: any[];
  /** Groups and sub-assets behind each template, for the rollup counts only. */
  groups?: any[];
  items?: any[];
  statusByTemplateId?: Map<number, AssetStatus>;
  workOrdersByTemplateId?: Map<number, number>;
  selectedRowKey?: string | null;
  onSelectRow: (row: AssetRow) => void;
  /** Cart mode, entered from the Edit button, adds the per-row Add control. */
  cartMode?: boolean;
  deployedTemplateIds?: number[];
  cartTemplateIds?: number[];
  onToggleCart?: (row: AssetRow) => void;
  onRemoveFromSite?: (row: AssetRow) => void;
}

const NO_VALUE = '—';

/**
 * Flat list of the templates available to a site. Groups and sub-assets are not
 * rows of their own here -- they are the subtree a template rolls its counts up
 * from, and the tree view is where the composition is inspected.
 */
const AssetListView: React.FC<AssetListViewProps> = ({
  templates,
  groups,
  items,
  statusByTemplateId,
  workOrdersByTemplateId,
  selectedRowKey,
  onSelectRow,
  cartMode = false,
  deployedTemplateIds = [],
  cartTemplateIds = [],
  onToggleCart,
  onRemoveFromSite,
}) => {
  const intl = useIntl();

  const deployed = useMemo(() => new Set(deployedTemplateIds.map(Number)), [deployedTemplateIds]);
  const inCart = useMemo(() => new Set(cartTemplateIds.map(Number)), [cartTemplateIds]);

  const rows = useMemo(
    () =>
      buildAssetRows(templates, [], [], {
        groups,
        items,
        statusByTemplateId,
        workOrdersByTemplateId,
      }),
    [templates, groups, items, statusByTemplateId, workOrdersByTemplateId]
  );

  if (!rows.length) {
    return (
      <div className="assets-page__empty">
        <p>{intl.formatMessage({ id: 'assets.list.empty' })}</p>
      </div>
    );
  }

  return (
    <div className="asset-list-view">
      <table className="asset-list-view__table">
        <thead>
          <tr>
            <th>{intl.formatMessage({ id: 'assets.list.name' })}</th>
            <th>{intl.formatMessage({ id: 'assets.list.description' })}</th>
            <th>{intl.formatMessage({ id: 'assets.list.area' })}</th>
            <th>{intl.formatMessage({ id: 'assets.list.status' })}</th>
            <th>{intl.formatMessage({ id: 'assets.list.workOrders' })}</th>
            {cartMode && <th />}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr
              key={row.key}
              className={selectedRowKey === row.key ? 'asset-list-view__row--selected' : ''}
              onClick={() => onSelectRow(row)}
            >
              <td className="asset-list-view__name">
                {/* Inner wrapper does the flex: a display:flex <td> stops being
                    a table cell and its border stops lining up with the row. */}
                <span className="asset-list-view__name-inner">
                  <span className="asset-list-view__thumb">
                    {row.imageUrl ? (
                      <img src={row.imageUrl} alt="" loading="lazy" />
                    ) : (
                      <i className="uil uil-image" />
                    )}
                  </span>
                  <span>{row.name}</span>
                </span>
              </td>
              <td className="asset-list-view__description">{row.description || NO_VALUE}</td>
              <td className="asset-list-view__area">
                {row.area === UNASSIGNED_AREA ? (
                  NO_VALUE
                ) : (
                  <>
                    <i className="uil uil-layer-group" />
                    <span>{row.area}</span>
                  </>
                )}
              </td>
              <td>
                <AssetStatusPill status={row.status} />
              </td>
              <td className="asset-list-view__work-orders">
                {row.workOrders > 0 ? <span>{row.workOrders}</span> : NO_VALUE}
              </td>
              {cartMode && (
                <td className="asset-list-view__cart-cell">
                  {deployed.has(row.id) ? (
                    <span className="asset-cart-cell__added">
                      {/* Same button treatment as the Done action in the task
                          admin table: shared .button green small. */}
                      <span className="button green small asset-cart-btn--added">
                        <i className="uil uil-check-circle" />
                        {intl.formatMessage({ id: 'assets.cart.added' })}
                      </span>
                      <button
                        type="button"
                        className="asset-cart-btn__remove"
                        onClick={(event) => {
                          event.stopPropagation();
                          if (onRemoveFromSite) onRemoveFromSite(row);
                        }}
                        aria-label={intl.formatMessage({ id: 'assets.cart.removeFromSite' })}
                        title={intl.formatMessage({ id: 'assets.cart.removeFromSite' })}
                      >
                        <i className="uil uil-trash-alt" />
                      </button>
                    </span>
                  ) : (
                    <button
                      type="button"
                      className={`asset-cart-btn ${
                        inCart.has(row.id) ? 'asset-cart-btn--in-cart' : 'asset-cart-btn--add'
                      }`}
                      onClick={(event) => {
                        event.stopPropagation();
                        if (onToggleCart) onToggleCart(row);
                      }}
                    >
                      <i className={`uil uil-${inCart.has(row.id) ? 'shopping-cart' : 'plus'}`} />
                      {inCart.has(row.id)
                        ? intl.formatMessage({ id: 'assets.cart.inCart' })
                        : intl.formatMessage({ id: 'assets.cart.add' })}
                    </button>
                  )}
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default AssetListView;
